import React from 'react';
import {
    Banknote, CreditCard, Smartphone, Wallet, Gift, Receipt, Users, UserPlus, CalendarDays, TrendingUp,
    PackageOpen, Landmark, ArrowDownLeft, ArrowUpRight, Scissors, ShoppingBag, Crown, Percent, AlertTriangle, CheckCircle2,
    Equal, Minus, Clock, Truck,
} from 'lucide-react';
import styles from './DailyReportView.module.css';
import { inr, longDate, timeOf, dateTimeOf, hourLabel } from '../../utils/money';

/** One row with a share bar (payments, sales mix) */
const BarRow = ({ icon: Icon, label, value, max, tone = 'primary', note }) => (
    <div className={styles.barRow}>
        <span className={`${styles.barIcon} ${styles[`tone_${tone}`]}`}><Icon size={14} /></span>
        <div className={styles.barBody}>
            <div className={styles.barTop}><span>{label}</span><b>{inr(value)}</b></div>
            <div className={styles.barTrack}><i className={styles[`fill_${tone}`]} style={{ width: `${max > 0 ? Math.max(value > 0 ? 3 : 0, (value / max) * 100) : 0}%` }} /></div>
            {note && <small>{note}</small>}
        </div>
    </div>
);

const Line = ({ label, value, sign, strong, muted, hint }) => {
    if (!strong && !value && muted) return null;
    return (
        <div className={`${styles.line} ${strong ? styles.lineStrong : ''}`}>
            <span>{label}{hint && <small>{hint}</small>}</span>
            <b className={sign === '-' && value ? styles.neg : sign === '+' && value ? styles.pos : ''}>
                {sign && value ? (sign === '-' ? '− ' : '+ ') : ''}{inr(value)}
            </b>
        </div>
    );
};

/** Sections of the report: the daily report page shows them as jump tabs */
export const REPORT_SECTIONS = [
    { id: 'rep-summary', label: 'Summary' },
    { id: 'rep-money', label: 'Money' },
    { id: 'rep-drawer', label: 'Cash drawer' },
    { id: 'rep-sales', label: 'Sales' },
    { id: 'rep-staff', label: 'Staff' },
    { id: 'rep-expenses', label: 'Expenses' },
    { id: 'rep-bills', label: 'Bills' },
];

export default function DailyReportView({ report, publicMode = false }) {
    if (!report) return null;
    const { totals: t, payments: p, sales: s, drawer: d, outside, expenses, store } = report;
    const r = report.dayResult;
    const catMax = Math.max(...(expenses.byCategory || []).map((c) => c.amount), 1);
    const payMax = Math.max(p.cash, p.upi, p.card, p.wallet, p.prepaidCard, 1);
    const mixMax = Math.max(s.services, s.products, s.loyaltySold, s.packageServicesValue, 1);
    const hourMax = Math.max(...report.hourly.map((h) => h.amount), 1);
    const closing = d.closing;
    const diff = closing ? closing.difference : null;
    const vendor = expenses.vendor || null;          // money paid to vendors today (part of expenses)
    const showCardCut = report.staff.some((x) => x.cardAdjust > 0);

    return (
        <div className={styles.report}>
            {/* ---------- header ---------- */}
            <header className={styles.head}>
                <div className={styles.storeBlock}>
                    {store?.logo ? <img src={store.logo} alt="" className={styles.logo} /> : <span className={styles.logoMark}>{(store?.name || 'S').charAt(0)}</span>}
                    <div>
                        <h1>{store?.name || 'Daily report'}</h1>
                        {store?.address && <p>{store.address}</p>}
                    </div>
                </div>
                <div className={styles.headRight}>
                    <span className={styles.kicker}>Daily closing report</span>
                    <strong>{longDate(report.date)}</strong>
                    <span className={`${styles.status} ${closing ? styles.statusClosed : report.isToday ? styles.statusLive : styles.statusOpen}`}>
                        {closing ? 'Drawer closed' : report.isToday ? 'Live, day not closed' : 'Drawer not closed'}
                    </span>
                </div>
            </header>

            {/* ---------- headline numbers ---------- */}
            <section className={styles.kpis} id="rep-summary">
                <div className={`${styles.kpi} ${styles.kpiHero}`}>
                    <span><TrendingUp size={14} /> Total sales</span>
                    <strong>{inr(t.grossSales)}</strong>
                    <small>{t.bills} {t.bills === 1 ? 'bill' : 'bills'}, average {inr(t.avgBill)}</small>
                </div>
                <div className={styles.kpi}>
                    <span><Banknote size={14} /> Fresh collection</span>
                    <strong className={styles.pos}>{inr(t.freshCollection)}</strong>
                    <small>Cash + UPI + card</small>
                </div>
                <div className={styles.kpi}>
                    <span><Users size={14} /> Clients served</span>
                    <strong>{t.customersServed}</strong>
                    <small><UserPlus size={11} /> {t.newClients} new</small>
                </div>
                <div className={styles.kpi}>
                    <span><Scissors size={14} /> Services done</span>
                    <strong>{t.serviceCount}</strong>
                    <small>{t.packageServiceCount} from packages</small>
                </div>
                <div className={styles.kpi}>
                    <span><CalendarDays size={14} /> Appointments</span>
                    <strong>{t.appointments ?? '—'}</strong>
                    <small>Booked for today</small>
                </div>
            </section>

            {/* ---------- today's result: in - expenses = left ---------- */}
            {r && (
                <section className={styles.resultCard}>
                    <h2>Today's result</h2>
                    <div className={styles.resultFlow}>
                        <div className={styles.resIn}>
                            <span><ArrowDownLeft size={14} /> Money in</span>
                            <strong>{inr(r.moneyIn)}</strong>
                            <small>Cash {inr(r.cashIn)} · Online {inr(r.onlineIn)}</small>
                        </div>
                        <i className={styles.op}><Minus size={16} /></i>
                        <div className={styles.resOut}>
                            <span><ArrowUpRight size={14} /> Expenses paid</span>
                            <strong>{inr(r.expensesTotal)}</strong>
                            <small>Cash {inr(r.expensesCash)} · Online {inr(r.expensesOnline)}</small>
                        </div>
                        <i className={styles.op}><Equal size={16} /></i>
                        <div className={`${styles.resNet} ${r.net < 0 ? styles.resNetLoss : ''}`}>
                            <span>{r.net < 0 ? 'Spent more than earned' : 'Left today'}</span>
                            <strong>{inr(r.net)}</strong>
                            <small>Before salaries and rent paid outside Hirsta</small>
                        </div>
                    </div>
                    <div className={styles.resultFacts}>
                        <div className={r.cashInDrawer < -0.009 ? styles.factRed : ''}><Landmark size={13} /><span>{r.cashInDrawer < -0.009 ? 'Drawer below zero' : 'Cash in the drawer'}</span><b>{inr(r.cashInDrawer)}</b></div>
                        <div><ArrowUpRight size={13} /><span>Deposited in bank</span><b>{inr(r.depositedToBank)}</b></div>
                        <div><Wallet size={13} /><span>Owner took cash</span><b>{inr(r.ownerTook)}</b></div>
                        <div className={r.udhaarGiven > 0 ? styles.factRed : ''}><AlertTriangle size={13} /><span>Udhaar given</span><b>{inr(r.udhaarGiven)}</b></div>
                        {r.boughtOnCredit > 0 && <div className={styles.factRed}><Truck size={13} /><span>Bought on credit (vendors)</span><b>{inr(r.boughtOnCredit)}</b></div>}
                    </div>
                </section>
            )}

            <div className={styles.grid2}>
                {/* ---------- payments ---------- */}
                <section className={styles.card} id="rep-money">
                    <h2>Money received</h2>
                    <BarRow icon={Banknote} label="Cash" value={p.cash} max={payMax} tone="green" />
                    <BarRow icon={Smartphone} label="UPI" value={p.upi} max={payMax} tone="primary" />
                    <BarRow icon={CreditCard} label="Card" value={p.card} max={payMax} tone="dark" />
                    <BarRow icon={Wallet} label="Khata advance used" value={p.wallet} max={payMax} tone="amber" note="Paid earlier, used today" />
                    <BarRow icon={Gift} label="Prepaid card used" value={p.prepaidCard} max={payMax} tone="pink" note="Paid earlier, used today" />
                    <div className={styles.sumRow}><span>Fresh collection (cash + UPI + card)</span><b>{inr(p.freshCollection)}</b></div>
                    <div className={styles.chips}>
                        <span className={styles.chipRed}><AlertTriangle size={12} /> Udhaar given {inr(p.dueCreated)}</span>
                        <span className={styles.chipGreen}><ArrowDownLeft size={12} /> Advance kept {inr(p.advanceKept)}</span>
                        <span className={styles.chipPurple}><PackageOpen size={12} /> Package services {inr(s.packageServicesValue)}</span>
                    </div>
                    {outside.total > 0 && (
                        <div className={styles.subBlock}>
                            <h3>Collected outside bills</h3>
                            <p className={styles.subNote}>Old udhaar paid back or advance added at the desk</p>
                            <div className={styles.miniGrid}>
                                <span>Cash <b>{inr(outside.cash)}</b></span><span>UPI <b>{inr(outside.upi)}</b></span><span>Card <b>{inr(outside.card)}</b></span>
                            </div>
                        </div>
                    )}
                </section>

                {/* ---------- cash drawer ---------- */}
                <section className={`${styles.card} ${styles.drawerCard}`} id="rep-drawer">
                    <h2><Landmark size={15} /> Cash drawer (galla)</h2>
                    <Line label="Opening cash" value={d.opening} hint={d.openingSource} />
                    <Line label="Cash from bills" value={d.cashSales} sign="+" />
                    <Line label="Old udhaar / advance in cash" value={d.outsideCash} sign="+" muted />
                    <Line label="Cash given by owner / other in" value={d.ownerCashIn} sign="+" muted />
                    <Line label="Deposited in bank" value={d.bankDeposit} sign="-" muted />
                    <Line label="Petty expenses" value={d.pettyExpense} sign="-" muted />
                    <Line label="Owner took cash" value={d.ownerWithdrawal} sign="-" muted />
                    <Line label="Other cash out" value={d.otherOut} sign="-" muted />
                    <Line label={vendor?.paidCash ? 'Expenses + vendors paid in cash' : 'Expenses paid in cash'} value={d.expensesCash} sign="-" muted hint={vendor?.paidCash ? `Vendors ${inr(vendor.paidCash)}` : ''} />
                    <Line label="Should be in the drawer" value={d.expected} strong />
                    {d.expected < -0.009 && (
                        <div className={styles.belowZero}>
                            <AlertTriangle size={14} />
                            <span>The drawer is below zero: {inr(Math.abs(d.expected))} was paid from someone's pocket. See the cash entries for who.</span>
                        </div>
                    )}
                    {closing && (
                        <div className={`${styles.closeBox} ${Math.abs(diff) < 0.01 ? styles.closeOk : diff < 0 ? styles.closeShort : styles.closeExtra}`}>
                            <div><span>Counted</span><b>{inr(closing.countedCash)}</b></div>
                            <div>
                                <span>{Math.abs(diff) < 0.01 ? 'Matches' : diff < 0 ? 'Short' : 'Extra'}</span>
                                <b>{Math.abs(diff) < 0.01 ? <CheckCircle2 size={16} /> : inr(Math.abs(diff))}</b>
                            </div>
                            {!publicMode && closing.closedByName && <small>Closed by {closing.closedByName} at {timeOf(closing.closedAt)}</small>}
                            {closing.note && <small>Note: {closing.note}</small>}
                        </div>
                    )}
                </section>
            </div>

            <div className={styles.grid2}>
                {/* ---------- sales mix ---------- */}
                <section className={styles.card} id="rep-sales">
                    <h2>What was sold</h2>
                    <BarRow icon={Scissors} label="Services" value={s.services} max={mixMax} tone="primary" />
                    <BarRow icon={ShoppingBag} label="Products" value={s.products} max={mixMax} tone="dark" />
                    <BarRow icon={Crown} label="Memberships and packages sold" value={s.loyaltySold} max={mixMax} tone="pink" />
                    <BarRow icon={PackageOpen} label="Package services given" value={s.packageServicesValue} max={mixMax} tone="amber" note="Already paid when the package was sold" />
                    <div className={styles.miniGrid}>
                        <span><Percent size={12} /> Discounts <b>{inr(s.discounts)}</b></span>
                        <span><Receipt size={12} /> GST collected <b>{inr(s.gst)}</b></span>
                        <span>Net revenue <b>{inr(t.netRevenue)}</b></span>
                    </div>
                </section>

                {/* ---------- hourly ---------- */}
                <section className={styles.card}>
                    <h2>Sales by hour</h2>
                    <div className={styles.hours}>
                        {report.hourly.map((h) => (
                            <div key={h.hour} className={styles.hour} title={`${hourLabel(h.hour)}: ${inr(h.amount)}, ${h.bills} bills`}>
                                <i style={{ height: `${(h.amount / hourMax) * 100}%` }} className={h.amount === hourMax && h.amount > 0 ? styles.hourPeak : ''} />
                                <span>{hourLabel(h.hour)}</span>
                            </div>
                        ))}
                    </div>
                </section>
            </div>

            <div className={styles.grid2}>
                {/* ---------- staff ---------- */}
                <section className={styles.card} id="rep-staff">
                    <h2>Staff today</h2>
                    {report.staff.length === 0 ? <p className={styles.empty}>No staff work recorded.</p> : (
                        <div className={styles.tableScroll}>
                            <table className={styles.table}>
                                <thead><tr><th>Staff</th><th>Services</th><th>Sales</th>{showCardCut && <th title="Services paid by prepaid card count at card value">Card cut</th>}<th>Package credit</th><th>Total credit</th></tr></thead>
                                <tbody>
                                    {report.staff.map((x) => (
                                        <tr key={x.name}>
                                            <td>{x.name}</td><td>{x.services}</td><td>{inr(x.sales)}</td>
                                            {showCardCut && <td className={x.cardAdjust > 0 ? styles.neg : ''}>{x.cardAdjust > 0 ? `− ${inr(x.cardAdjust)}` : '—'}</td>}
                                            <td>{inr(x.packageCredit)}</td><td><b>{inr(x.credit)}</b></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>

                {/* ---------- top services + memberships ---------- */}
                <section className={styles.card}>
                    <h2>Top services</h2>
                    {report.topServices.length === 0 ? <p className={styles.empty}>No services today.</p> : (
                        <ol className={styles.rank}>
                            {report.topServices.map((x, i) => <li key={x.name}><span>{i + 1}</span><em>{x.name}</em><small>{x.count}×</small><b>{inr(x.amount)}</b></li>)}
                        </ol>
                    )}
                    {report.loyaltySold.length > 0 && (
                        <div className={styles.subBlock}>
                            <h3>Memberships and packages sold</h3>
                            {report.loyaltySold.map((x, i) => <div key={i} className={styles.line}><span>{x.name}{x.qty > 1 ? ` × ${x.qty}` : ''}<small>{x.client}</small></span><b>{inr(x.amount)}</b></div>)}
                        </div>
                    )}
                </section>
            </div>

            {expenses.available && expenses.items?.length > 0 ? (
                <section className={styles.card} id="rep-expenses">
                    <h2>Expenses today</h2>
                    <div className={styles.grid2Inner}>
                        <div>
                            {(expenses.byCategory || []).map((c) => (
                                <BarRow key={c.name} icon={Receipt} label={c.name} value={c.amount} max={catMax} tone="pink" />
                            ))}
                        </div>
                        <div className={styles.expSummary}>
                            <div><span>Paid in cash (from the drawer)</span><b className={styles.neg}>{inr(expenses.cash)}</b></div>
                            <div><span>Paid by UPI / card / bank</span><b>{inr(expenses.nonCash)}</b></div>
                            {d.pettyExpense > 0 && <div><span>Petty cash (cash drawer)</span><b className={styles.neg}>{inr(d.pettyExpense)}</b></div>}
                            {vendor && vendor.paid > 0 && (
                                <div><span><Truck size={12} style={{ verticalAlign: '-2px' }} /> Of which paid to vendors{vendor.notCleared > 0 ? ` (${inr(vendor.notCleared)} not cleared)` : ''}</span><b>{inr(vendor.paid)}</b></div>
                            )}
                            {vendor && vendor.refundCash > 0 && <div><span>Cash refunded by vendors</span><b className={styles.pos}>+ {inr(vendor.refundCash)}</b></div>}
                            <div className={styles.expTotal}><span>Total spent</span><b>{inr(expenses.total + (d.pettyExpense || 0))}</b></div>
                            {expenses.pendingCount > 0 && <p className={styles.pendingNote}><Clock size={12} /> {expenses.pendingCount} {expenses.pendingCount === 1 ? 'expense is' : 'expenses are'} waiting for approval ({inr(expenses.pending)})</p>}
                        </div>
                    </div>
                </section>
            ) : (
                <section className={styles.card} id="rep-expenses">
                    <h2>Expenses today</h2>
                    <p className={styles.empty}>No expenses recorded today.{d.pettyExpense > 0 ? ` Petty cash from the drawer: ${inr(d.pettyExpense)}.` : ''}</p>
                </section>
            )}

            {report.vendorPurchases?.count > 0 && (
                <section className={styles.card}>
                    <h2><Truck size={15} /> Vendor purchases today</h2>
                    <div className={styles.miniGrid} style={{ marginTop: 0, marginBottom: 10 }}>
                        <span>Bought <b>{inr(report.vendorPurchases.total)}</b></span>
                        <span>Paid <b className={styles.pos}>{inr(report.vendorPurchases.paid)}</b></span>
                        <span>To pay later <b className={report.vendorPurchases.toPay > 0 ? styles.neg : ''}>{inr(report.vendorPurchases.toPay)}</b></span>
                    </div>
                    <div className={styles.tableScroll}>
                        <table className={styles.table}>
                            <thead><tr><th>Vendor</th><th>Products</th><th>Paid by</th><th style={{ textAlign: 'right' }}>Total</th><th style={{ textAlign: 'right' }}>To pay</th></tr></thead>
                            <tbody>
                                {report.vendorPurchases.items.map((b) => (
                                    <tr key={b.id}>
                                        <td><b>{b.vendorName}</b>{b.billNo && <small className={styles.phone}>Bill {b.billNo}</small>}</td>
                                        <td>{b.products.slice(0, 4).map((x) => `${x.qty} × ${x.name}`).join(', ')}{b.products.length > 4 ? ` +${b.products.length - 4} more` : ''}{b.gst > 0 && <small className={styles.phone}>GST {inr(b.gst)}</small>}</td>
                                        <td>{b.paidVia.length ? b.paidVia.map((x, i) => <span key={i} style={{ display: 'block' }}>{x.modeLabel} {inr(x.amount)}{!x.cleared && <span className={styles.tagWarn}>Not cleared</span>}</span>) : 'Not paid yet'}</td>
                                        <td style={{ textAlign: 'right' }}><b>{inr(b.total)}</b></td>
                                        <td style={{ textAlign: 'right' }} className={b.toPay > 0 ? styles.neg : styles.pos}>{b.toPay > 0 ? inr(b.toPay) : 'Paid'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {report.vendorPurchases.toPay > 0 && <p className={styles.subNote} style={{ marginTop: 8 }}>Bought on credit is owed to vendors and is not part of today's expenses until it is paid.</p>}
                </section>
            )}

            {(d.entries.length > 0 || expenses.items?.length > 0) && (
                <section className={styles.card}>
                    <h2>Cash entries and expenses</h2>
                    <table className={styles.table}>
                        <thead><tr><th>Time</th><th>Type</th><th>Note</th>{!publicMode && <th>By</th>}<th style={{ textAlign: 'right' }}>Amount</th></tr></thead>
                        <tbody>
                            {d.entries.map((e) => (
                                <tr key={e._id} className={e.voided ? styles.voided : ''}>
                                    <td>{timeOf(e.createdAt)}</td>
                                    <td>{e.direction === 'IN' ? <ArrowDownLeft size={12} className={styles.pos} /> : <ArrowUpRight size={12} className={styles.neg} />} {e.label}{e.voided ? ' (cancelled)' : ''}{e.wentNegative && !e.voided && <span className={styles.tagWarn}>Below zero</span>}</td>
                                    <td>{[e.note, e.reference && `Ref ${e.reference}`].filter(Boolean).join(' · ') || '—'}</td>
                                    {!publicMode && <td>{e.createdByName}</td>}
                                    <td style={{ textAlign: 'right' }} className={e.direction === 'IN' ? styles.pos : styles.neg}>{e.direction === 'IN' ? '+' : '−'} {inr(e.amount)}</td>
                                </tr>
                            ))}
                            {(expenses.items || []).map((x, i) => {
                                const cash = !x.mode || x.mode === 'CASH';
                                return (
                                    <tr key={`x${i}`}>
                                        <td>{timeOf(x.at) || '—'}</td>
                                        <td>{x.vendor ? 'Vendor payment' : 'Expense'}, {cash ? 'paid in cash' : `paid by ${x.modeLabel}`}{x.status === 'pending' ? ' (pending approval)' : ''}{x.clearPending ? ' (not cleared yet)' : ''}</td>
                                        <td>{x.title}{x.category ? <small className={styles.phone}>{x.category}{x.subcategory ? ` · ${x.subcategory}` : ''}</small> : null}</td>
                                        {!publicMode && <td>{x.by || '—'}</td>}
                                        <td style={{ textAlign: 'right' }} className={cash ? styles.neg : ''}>{cash ? '− ' : ''}{inr(x.amount)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </section>
            )}

            {/* ---------- bills ---------- */}
            <section className={styles.card} id="rep-bills">
                <h2>All bills ({report.bills.length})</h2>
                {report.bills.length === 0 ? <p className={styles.empty}>No bills completed on this day.</p> : (
                    <div className={styles.tableScroll}>
                        <table className={styles.table}>
                            <thead><tr><th>Time</th><th>Bill</th><th>Client</th><th>Paid by</th><th style={{ textAlign: 'right' }}>Udhaar</th><th style={{ textAlign: 'right' }}>Total</th></tr></thead>
                            <tbody>
                                {report.bills.map((b) => (
                                    <tr key={b.invoiceNo}>
                                        <td>{timeOf(b.time)}</td><td className={styles.billNo}>{b.invoiceNo}</td>
                                        <td>{b.client}{b.phone && <small className={styles.phone}>{b.phone}</small>}</td>
                                        <td>{b.modes.join(', ') || (b.total ? '—' : 'Package')}</td>
                                        <td style={{ textAlign: 'right' }} className={b.due ? styles.neg : ''}>{b.due ? inr(b.due) : '—'}</td>
                                        <td style={{ textAlign: 'right' }}><b>{inr(b.total)}</b></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            <footer className={styles.foot}>Generated {dateTimeOf(report.generatedAt)} · Powered by Hirsta</footer>
        </div>
    );
}
